import { useState, useEffect } from "react";
import {
  Card, Table, Stack, TableRow, TableBody, TableCell, Container, Typography,
  TableContainer, Select, TextField, FormControl, InputLabel, MenuItem,
  Grid, Chip, CircularProgress, Button,
} from "@mui/material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "src/components/common/ListHead";
import MyPagination from "src/components/common/MyPagination";
import UserInfo from "src/components/common/UserInfo";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";

const TYPE_OPTIONS = [
  { value: 1, label: "金豆" },
  { value: 2, label: "银豆" },
];

const STATUS_OPTIONS = [
  { value: 0, label: "待抢购" },
  { value: 1, label: "已购买" },
  { value: 2, label: "已寄卖" },
  { value: 3, label: "已卖出" },
  { value: 4, label: "已释放" },
];

const STATUS_COLORS = {
  0: "default",
  1: "primary",
  2: "warning",
  3: "success",
  4: "info",
};

const TABLE_HEAD = [
  { id: "id", label: "ID", alignRight: false },
  { id: "session_id", label: "场次", alignRight: false },
  { id: "user_id", label: "用户", alignRight: false },
  { id: "plat_id", label: "鲸世优选UID", alignRight: false },
  { id: "type", label: "类型", alignRight: false },
  { id: "num", label: "金豆数量", alignRight: false },
  { id: "real_num", label: "实际扣减", alignRight: false },
  { id: "income_num", label: "收益数量", alignRight: false },
  { id: "status", label: "状态", alignRight: false },
  { id: "sell_time", label: "最后操作时间", alignRight: false },
];

const todayStr = new Date().toISOString().split("T")[0];

export default function OrderList() {
  const [filter, setFilter] = useState({
    sessionName: "",
    sessionId: "",
    status: "",
    userId: "",
    platId: "",
    date: todayStr,
    pageNum: 1,
    pageSize: 20,
  });
  const [sessions, setSessions] = useState([]);
  const [list, setList] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchSessions = async () => {
    try {
      const res = await asyncRequest({
        path: "/jingxi/game/sessions/list",
        data: { date: filter.date, pageNum: 1, pageSize: 200 },
      });
      if (res.code === 1) {
        setSessions(res.data || []);
      }
    } catch (err) {
      console.error("获取场次列表失败", err);
    }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const data = {
        sessionName: filter.sessionName || undefined,
        sessionId: filter.sessionId || undefined,
        status: filter.status || undefined,
        userId: filter.userId || undefined,
        platId: filter.platId || undefined,
        date: filter.date || undefined,
        pageNum: filter.pageNum,
        pageSize: filter.pageSize,
      };
      const res = await asyncRequest({ path: "/jingxi/game/orders/list", data });
      if (res.code === 1) {
        setList(res.data || []);
        setCount(res.count || 0);
      } else {
        alert(res.msg || "获取列表失败");
      }
    } catch (err) {
      console.error(err);
      alert("获取列表失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); fetchSessions(); }, []);

  const renderType = (type) => {
    const opt = TYPE_OPTIONS.find((o) => o.value === type);
    return opt ? opt.label : "-";
  };

  const renderStatus = (status) => {
    const opt = STATUS_OPTIONS.find((o) => o.value === status);
    return opt ? (
      <Chip label={opt.label} color={STATUS_COLORS[status] || "default"} size="small" />
    ) : (
      "-"
    );
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "-";
    const d = new Date(parseInt(timestamp) * 1000);
    return d.toLocaleString("zh-CN", { hour12: false });
  };

  return (
    <Page title="游戏订单">
      <Container maxWidth="2000px">
        <Card sx={{ p: 3 }}>
          <Typography variant="h4" sx={{ mb: 3 }}>游戏订单列表</Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>场次名</InputLabel>
                <Select
                  value={filter.sessionName}
                  label="场次名"
                  onChange={(e) => {
                    setFilter({ ...filter, sessionName: e.target.value, pageNum: 1 });
                    setTimeout(() => fetchList(), 0);
                  }}
                >
                  <MenuItem value="">全部</MenuItem>
                  {sessions.map((s) => (
                    <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                size="small"
                label="场次ID"
                value={filter.sessionId}
                onChange={(e) => setFilter({ ...filter, sessionId: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") fetchList(); }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="日期"
                InputLabelProps={{ shrink: true }}
                value={filter.date}
                onChange={(e) => setFilter({ ...filter, date: e.target.value, pageNum: 1 })}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>状态</InputLabel>
                <Select
                  value={filter.status}
                  label="状态"
                  onChange={(e) => {
                    setFilter({ ...filter, status: e.target.value, pageNum: 1 });
                    setTimeout(() => fetchList(), 0);
                  }}
                >
                  <MenuItem value="">全部</MenuItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                size="small"
                label="金银通ID"
                value={filter.userId}
                onChange={(e) => setFilter({ ...filter, userId: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") fetchList(); }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                size="small"
                label="鲸世优选UID"
                value={filter.platId}
                onChange={(e) => setFilter({ ...filter, platId: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") fetchList(); }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={() => {
                    setFilter({ sessionName: "", sessionId: "", status: "", userId: "", platId: "", date: todayStr, pageNum: 1, pageSize: 20 });
                  }}
                >
                  重置
                </Button>
                <Button variant="contained" size="small" fullWidth onClick={fetchList}>
                  搜索
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 5 }}><CircularProgress /></Stack>
          ) : list.length === 0 ? (
            <SearchNotFound searchQuery={filter} />
          ) : (
            <>
              <Scrollbar>
                <TableContainer>
                  <Table>
                    <ListHead headLabel={TABLE_HEAD} />
                    <TableBody>
                      {list.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.id}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {row.session_name || `场次${row.session_id}`}
                            </Typography>
                            {row.session_grade_id && (
                              <Typography variant="caption" color="text.secondary">
                                ID:{row.session_id}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {row.user_nickname || row.mobile ? (
                              <UserInfo
                                uid={row.plat_id || row.user_id}
                                nickname={row.user_nickname || ""}
                                avatar={row.avatar}
                                mobile={row.mobile}
                              />
                            ) : (
                              row.user_id || "-"
                            )}
                          </TableCell>
                          <TableCell>{row.plat_id || "-"}</TableCell>
                          <TableCell>{renderType(row.type)}</TableCell>
                          <TableCell>{row.num}</TableCell>
                          <TableCell>{row.real_num || "-"}</TableCell>
                          <TableCell>{row.income_num || "-"}</TableCell>
                          <TableCell>{renderStatus(row.status)}</TableCell>
                          <TableCell>{formatTime(row.sell_time || row.create_time)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <MyPagination
                count={count}
                rowsPerPage={filter.pageSize}
                page={filter.pageNum}
                onPageChange={(val) => setFilter({ ...filter, pageNum: val })}
                onRowsPerPageChange={(val) => setFilter({ ...filter, pageSize: val, pageNum: 1 })}
              />
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
}
